import { StoreError } from '@/types/storeError.interface';
import {
  IApplication,
  IndexedSignatures,
  IndexedUsersFullName,
} from '@/typings';
import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '../store';

type IndexedSignatureNames = Record<IApplication['id'], string | null>;

interface SignatureState {
  loading: boolean;
  error: StoreError | null;
  usersFullName: IndexedUsersFullName;
  signatureNames: IndexedSignatureNames;
  signatures: IndexedSignatures;
}

const initialState: SignatureState = {
  loading: false,
  error: null,
  usersFullName: {},
  signatureNames: {},
  signatures: {},
};

export const signatureSlice = createSlice({
  name: 'signature',
  initialState,
  reducers: {
    signatureLoading: state => {
      state.loading = true;
    },
    fetchUserFullName: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        fullname: string;
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.usersFullName[action.payload.applicationId] =
        action.payload.fullname;
    },
    setSignatureName: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        signatureName: string | null;
      }>,
    ) => {
      state.signatureNames[action.payload.applicationId] =
        action.payload.signatureName;
    },
    fetchSignature: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        signature: string;
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.signatures[action.payload.applicationId] = action.payload.signature;
    },
    removeSignature: (state, action: PayloadAction<IApplication['id']>) => {
      state.loading = false;
      state.error = null;
      delete state.signatures[action.payload];
    },
    fetchSignatureFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  signatureLoading,
  fetchUserFullName,
  setSignatureName,
  fetchSignature,
  removeSignature,
  fetchSignatureFailure,
} = signatureSlice.actions;

export const selectSignature = (state: RootState) =>
  state[signatureSlice.name] as typeof initialState;
